/**
 * Browser wishlist: stores product handles in localStorage and syncs to customer metafields when logged in.
 */
(function () {
  function storageKey() {
    var el = document.querySelector('[data-wishlist-storage-key]');
    return (el && el.getAttribute('data-wishlist-storage-key')) || 'theme_wishlist_v1';
  }

  function getCustomerInfo() {
    var body = document.body;
    if (!body) return null;
    var loggedIn = body.getAttribute('data-customer-logged-in') === 'true';
    if (!loggedIn) return null;
    return {
      id: body.getAttribute('data-customer-id'),
      email: body.getAttribute('data-customer-email'),
      storefrontToken: body.getAttribute('data-storefront-token'),
      shopDomain: body.getAttribute('data-shop-domain'),
      savedWishlist: body.getAttribute('data-customer-wishlist')
    };
  }

  function readHandles() {
    try {
      var raw = localStorage.getItem(storageKey());
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeHandles(handles) {
    try {
      localStorage.setItem(storageKey(), JSON.stringify(handles));
    } catch (e) {}
    
    // Sync to customer metafield if logged in
    var customer = getCustomerInfo();
    if (customer && customer.id && customer.storefrontToken) {
      saveWishlistToMetafield(handles, customer);
    }
    
    window.dispatchEvent(
      new CustomEvent('wishlist:updated', { detail: { count: handles.length, handles: handles.slice() } })
    );
  }

  // Save wishlist and sync to email marketing platforms
  function saveWishlistToMetafield(handles, customer) {
    // Store the customer email with wishlist for email marketing integration
    try {
      localStorage.setItem(storageKey() + '-email', customer.email || '');
      localStorage.setItem(storageKey() + '-customer-id', customer.id || '');
      localStorage.setItem(storageKey() + '-updated', new Date().toISOString());
    } catch (e) {}
    
    // Dispatch event for custom integrations
    window.dispatchEvent(new CustomEvent('wishlist:sync', {
      detail: {
        customerId: customer.id,
        customerEmail: customer.email,
        handles: handles.slice(),
        timestamp: new Date().toISOString()
      }
    }));
    
    // Track with Omnisend (if available)
    trackWishlistOmnisend(handles, customer);
    
    // Track with Klaviyo (if available)
    trackWishlistKlaviyo(handles, customer);
    
    // Sync to cart note for Shopify Flow access
    syncWishlistToCartNote(handles);
  }

  // Omnisend integration
  function trackWishlistOmnisend(handles, customer) {
    if (typeof omnisend === 'undefined' || !omnisend.push) return;
    
    try {
      // Identify the customer
      omnisend.push(['identify', {
        email: customer.email,
        properties: {
          wishlistItems: handles.join(','),
          wishlistCount: handles.length,
          wishlistUpdated: new Date().toISOString()
        }
      }]);
      
      // Track wishlist update event
      omnisend.push(['track', 'wishlist updated', {
        email: customer.email,
        wishlistItems: handles,
        wishlistCount: handles.length
      }]);
    } catch (e) {}
  }

  // Klaviyo integration
  function trackWishlistKlaviyo(handles, customer) {
    var _learnq = window._learnq || [];
    if (!_learnq.push) return;
    
    try {
      // Identify the customer
      _learnq.push(['identify', {
        '$email': customer.email,
        'Wishlist Items': handles.join(', '),
        'Wishlist Count': handles.length,
        'Wishlist Last Updated': new Date().toISOString()
      }]);
      
      // Track wishlist update event
      _learnq.push(['track', 'Wishlist Updated', {
        'Items': handles,
        'Count': handles.length,
        'Timestamp': new Date().toISOString()
      }]);
    } catch (e) {}
  }

  // Sync wishlist to cart note so Shopify Flow can access it
  function syncWishlistToCartNote(handles) {
    var customer = getCustomerInfo();
    if (!customer || !customer.email) return;
    
    var wishlistData = {
      email: customer.email,
      customerId: customer.id,
      items: handles,
      updatedAt: new Date().toISOString()
    };
    
    // Store as cart attribute (accessible via Shopify Flow when cart/checkout occurs)
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: {
          wishlist_data: JSON.stringify(wishlistData)
        }
      })
    }).catch(function() {});
  }

  // Track individual product add/remove for email automation
  function trackWishlistProductEvent(handle, action, customer) {
    if (!customer || !customer.email) return;
    
    // Fetch product details for richer tracking
    fetchProduct(handle).then(function(product) {
      if (!product) return;
      
      var eventData = {
        productHandle: handle,
        productTitle: product.title,
        productUrl: window.location.origin + '/products/' + handle,
        productImage: product.featured_image || (product.images && product.images[0]) || '',
        productPrice: product.price,
        customerEmail: customer.email,
        timestamp: new Date().toISOString()
      };
      
      // Omnisend
      if (typeof omnisend !== 'undefined' && omnisend.push) {
        omnisend.push(['track', action === 'add' ? 'added to wishlist' : 'removed from wishlist', eventData]);
      }
      
      // Klaviyo
      var _learnq = window._learnq || [];
      if (_learnq.push) {
        _learnq.push(['track', action === 'add' ? 'Added to Wishlist' : 'Removed from Wishlist', {
          'Product Name': product.title,
          'Product Handle': handle,
          'Product URL': eventData.productUrl,
          'Product Image': eventData.productImage,
          'Price': product.price / 100
        }]);
      }
      
      // Custom event for other integrations
      window.dispatchEvent(new CustomEvent('wishlist:product-' + action, { detail: eventData }));
    }).catch(function() {});
  }

  // Merge localStorage wishlist with saved metafield wishlist on page load
  function initializeWishlistFromMetafield() {
    var customer = getCustomerInfo();
    if (!customer || !customer.savedWishlist) return;
    
    try {
      var savedHandles = JSON.parse(customer.savedWishlist);
      if (!Array.isArray(savedHandles)) return;
      
      var localHandles = readHandles();
      
      // Merge: combine both lists, remove duplicates
      var merged = localHandles.slice();
      savedHandles.forEach(function(handle) {
        if (merged.indexOf(handle) === -1) {
          merged.push(handle);
        }
      });
      
      // Only update if there are differences
      if (merged.length !== localHandles.length) {
        try {
          localStorage.setItem(storageKey(), JSON.stringify(merged));
        } catch (e) {}
      }
    } catch (e) {}
  }

  function normalizeAjaxRoot(path) {
    if (!path || path === '/') return '/';
    var p = String(path).trim();
    return p.slice(-1) === '/' ? p : p + '/';
  }

  /** Locale-aware storefront path: Liquid sets body[data-shopify-ajax-root]; falls back to Shopify.routes.root. */
  function getRoot() {
    var body = document.body;
    if (body && body.getAttribute) {
      var fromLiquid = body.getAttribute('data-shopify-ajax-root');
      if (fromLiquid != null && fromLiquid !== '') {
        return normalizeAjaxRoot(fromLiquid);
      }
    }
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return normalizeAjaxRoot(window.Shopify.routes.root);
    }
    return '/';
  }

  /** Wishlist passes translated label via data attr; broken locales output "Translation missing" or the raw key. */
  function addToCartButtonLabelFromEl(el) {
    var fallback = 'Add to cart';
    if (!el || !el.getAttribute) return fallback;
    var raw = el.getAttribute('data-add-to-cart-label');
    if (raw == null || String(raw).trim() === '') return fallback;
    var s = String(raw);
    if (/translation missing/i.test(s)) return fallback;
    if (s === 'products.product.add_to_cart') return fallback;
    return s;
  }

  function moneyFormat() {
    var cfg = document.getElementById('cart-config');
    if (!cfg) return '${{amount}}';
    try {
      var o = JSON.parse(cfg.textContent || '{}');
      return o.money_format || '${{amount}}';
    } catch (e) {
      return '${{amount}}';
    }
  }

  function formatMoney(cents) {
    var fmt = moneyFormat();
    var n = Number(cents);
    if (!isFinite(n)) n = 0;
    try {
      if (window.theme && window.theme.Helpers && typeof window.theme.Helpers.formatMoney === 'function') {
        return window.theme.Helpers.formatMoney(n, fmt);
      }
    } catch (e) {
      /* Shopify money_with_currency_format can omit {{amount}} tokens; theme formatMoney then throws. */
    }
    return (n / 100).toFixed(2);
  }

  /**
   * Shopify /products/{handle}.js returns variant.price & compare_at_price in minor units (cents).
   */
  function priceToCents(val) {
    if (val == null || val === '') return 0;
    if (typeof val === 'number' && Number.isFinite(val)) {
      return Math.round(val);
    }
    var s = String(val).trim();
    if (s.indexOf('.') !== -1) {
      var dec = parseFloat(s.replace(/[^0-9.]/g, ''));
      return isNaN(dec) ? 0 : Math.round(dec * 100);
    }
    var whole = parseInt(s.replace(/\D/g, ''), 10);
    return isNaN(whole) ? 0 : whole;
  }

  function pickVariant(product) {
    var variants = product.variants || [];
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].available) return variants[i];
    }
    return variants[0] || {};
  }

  window.WishlistTheme = {
    getHandles: readHandles,
    has: function (handle) {
      return readHandles().indexOf(handle) !== -1;
    },
    add: function (handle) {
      if (!handle) return;
      var h = readHandles();
      if (h.indexOf(handle) === -1) {
        h.push(handle);
        writeHandles(h);
        // Track product added event for email marketing
        var customer = getCustomerInfo();
        if (customer) {
          trackWishlistProductEvent(handle, 'add', customer);
        }
      }
    },
    remove: function (handle) {
      var hadItem = readHandles().indexOf(handle) !== -1;
      var h = readHandles().filter(function (x) {
        return x !== handle;
      });
      writeHandles(h);
      // Track product removed event for email marketing
      if (hadItem) {
        var customer = getCustomerInfo();
        if (customer) {
          trackWishlistProductEvent(handle, 'remove', customer);
        }
      }
    },
    toggle: function (handle) {
      if (!handle) return false;
      if (this.has(handle)) {
        this.remove(handle);
        return false;
      }
      this.add(handle);
      return true;
    },
    // Get customer wishlist data for external use
    getCustomerData: function() {
      var customer = getCustomerInfo();
      return {
        isLoggedIn: !!customer,
        email: customer ? customer.email : null,
        customerId: customer ? customer.id : null,
        handles: readHandles()
      };
    }
  };

  function updateCountBadges() {
    var n = readHandles().length;
    document.querySelectorAll('.js-wishlist-count').forEach(function (el) {
      el.innerHTML = String(n);
    });
  }

  function syncWishlistDrawerFooter() {
    var li = document.querySelector('.cart-links__link-wishlist[data-wishlist-nav-url]');
    var wrap = document.querySelector('.js-wishlist-drawer-footer');
    var a = document.querySelector('.wishlist-drawer__full-link');
    if (!wrap || !a) return;
    var url = li ? li.getAttribute('data-wishlist-nav-url') : '';
    if (url) {
      a.setAttribute('href', url);
      wrap.classList.remove('hide');
    } else {
      wrap.classList.add('hide');
    }
  }

  function renderWishlistDrawer() {
    var drawerRoot = document.getElementById('wishlist-drawer-root');
    if (!drawerRoot) return;
    var grid = drawerRoot.querySelector('[data-wishlist-drawer-grid]');
    var emptyEl = drawerRoot.querySelector('[data-wishlist-drawer-empty]');
    if (!grid) return;

    var handles = readHandles();
    var removeLabel = drawerRoot.getAttribute('data-remove-label') || 'Remove';
    var soldLabel = drawerRoot.getAttribute('data-sold-label') || 'Sold out';
    var addToCartLabel = addToCartButtonLabelFromEl(drawerRoot);

    grid.innerHTML = '';

    if (handles.length === 0) {
      if (emptyEl) emptyEl.classList.remove('hide');
      return;
    }
    if (emptyEl) emptyEl.classList.add('hide');

    var chain = Promise.resolve();
    handles.forEach(function (handle) {
      chain = chain.then(function () {
        return fetchProduct(handle)
          .then(function (product) {
            if (!product) {
              window.WishlistTheme.remove(handle);
              return;
            }
            try {
              var v = pickVariant(product);
              var priceC = priceToCents(v.price);
              var compareC = priceToCents(v.compare_at_price);
              var onSale = compareC > priceC && compareC > 0;
              var available = product.available !== false && v.available !== false;
              var img = '';
              if (typeof product.featured_image === 'string') {
                img = product.featured_image;
              } else if (product.featured_image && product.featured_image.src) {
                img = product.featured_image.src;
              } else if (product.images && product.images[0]) {
                var im0 = product.images[0];
                img = typeof im0 === 'string' ? im0 : im0.src || '';
              }
              if (img && img.indexOf('//') === 0) {
                img = 'https:' + img;
              }
              var title = product.title || '';
              var url = product.url || '/products/' + handle;
              if (url.indexOf('http') !== 0 && url.charAt(0) !== '/') {
                url = '/' + url;
              }

              var priceHtml = '<span class="theme-money">' + formatMoney(priceC) + '</span>';
              if (onSale && available) {
                priceHtml +=
                  ' <span class="compare-at-price theme-money">' + formatMoney(compareC) + '</span>';
              }

              var addBtnDrawer = '';
              if (available && v.id) {
                addBtnDrawer =
                  '<button type="button" class="button add AddtoCart wishlist-drawer__add js-wishlist-add-to-cart" data-variant-id="' +
                  String(v.id) +
                  '">' +
                  escapeHtml(addToCartLabel) +
                  '</button>';
              }

              var row =
                '<div class="wishlist-drawer__row js-wishlist-drawer-row" data-product-handle="' +
                escapeHtml(handle) +
                '">' +
                '<a class="wishlist-drawer__thumb" href="' +
                escapeHtml(url) +
                '">' +
                (img
                  ? '<img src="' +
                    escapeHtml(img) +
                    '" alt="" width="80" height="80" loading="lazy">'
                  : '<span class="wishlist-drawer__thumb-placeholder"></span>') +
                '</a>' +
                '<div class="wishlist-drawer__meta">' +
                '<a class="wishlist-drawer__title" href="' +
                escapeHtml(url) +
                '">' +
                escapeHtml(title) +
                '</a>' +
                '<div class="wishlist-drawer__price">' +
                priceHtml +
                '</div>' +
                (!available ? '<p class="note mb0">' + escapeHtml(soldLabel) + '</p>' : '') +
                '<div class="wishlist-drawer__actions">' +
                addBtnDrawer +
                '<button type="button" class="button secondary-button wishlist-drawer__remove js-wishlist-remove-drawer" data-product-handle="' +
                escapeHtml(handle) +
                '">' +
                escapeHtml(removeLabel) +
                '</button>' +
                '</div>' +
                '</div></div>';

              grid.insertAdjacentHTML('beforeend', row);
            } catch (e) {}
          })
          .catch(function () {
            return null;
          });
      });
    });

    chain
      .then(function () {
        if (readHandles().length === 0 && emptyEl) emptyEl.classList.remove('hide');
        grid.querySelectorAll('.js-wishlist-remove-drawer').forEach(function (btn) {
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            var h = btn.getAttribute('data-product-handle');
            window.WishlistTheme.remove(h);
          });
        });
        bindWishlistAddToCartButtons(grid);
      })
      .catch(function () {});
  }

  function refreshWishlistViews() {
    updateCountBadges();
    initToggles();
    syncWishlistDrawerFooter();
    renderWishlistDrawer();
    scheduleRenderWishlistPage();
  }

  function initWishlistDrawerTriggers() {
    document.querySelectorAll('.js-wishlist-drawer-trigger').forEach(function (btn) {
      if (btn.dataset.wishlistDrawerBound) return;
      btn.dataset.wishlistDrawerBound = '1';
      btn.addEventListener('click', function () {
        renderWishlistDrawer();
      });
    });
  }

  function syncToggleButton(btn) {
    var handle = btn.getAttribute('data-product-handle');
    if (!handle) return;
    var on = window.WishlistTheme.has(handle);
    btn.classList.toggle('is-in-wishlist', on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    var addL = btn.getAttribute('data-label-add') || '';
    var remL = btn.getAttribute('data-label-remove') || '';
    var label = btn.querySelector('.wishlist-toggle__label');
    if (label) {
      label.textContent = on ? remL : addL;
    }
    btn.setAttribute('aria-label', on ? remL : addL);
  }

  function initToggles() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      syncToggleButton(btn);
      if (btn.dataset.wishlistBound) return;
      btn.dataset.wishlistBound = '1';
      btn.addEventListener('click', function () {
        var handle = btn.getAttribute('data-product-handle');
        window.WishlistTheme.toggle(handle);
        syncToggleButton(btn);
      });
    });
  }

  function addVariantToCart(variantId) {
    var id = Number(variantId);
    if (!id || !isFinite(id)) return Promise.reject(new Error('wishlist: bad variant'));
    var primary = getRoot() + 'cart/add.js';
    var fallback = '/cart/add.js';
    var urls = primary === fallback ? [primary] : [primary, fallback];
    function post(url) {
      return fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items: [{ id: id, quantity: 1 }] }),
      }).then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw data;
          return data;
        });
      });
    }
    return post(urls[0]).catch(function () {
      if (urls.length < 2) throw new Error('wishlist cart');
      return post(urls[1]);
    });
  }

  function afterWishlistAddToCart() {
    var cfgEl = document.getElementById('cart-config');
    if (!cfgEl || !window.Shopify || !window.Shopify.theme || !window.Shopify.theme.cart) return;
    try {
      var config = JSON.parse(cfgEl.textContent || '{}');
      if (typeof window.Shopify.theme.cart.getCart !== 'function') return;
      window.Shopify.theme.cart.getCart().then(function (cart) {
        if (
          window.Shopify.theme.ajaxCart &&
          typeof window.Shopify.theme.ajaxCart.updateView === 'function'
        ) {
          window.Shopify.theme.ajaxCart.updateView(config, cart);
        }
        if (
          config.cart_action === 'drawer' &&
          window.Shopify.theme.ajaxCart &&
          typeof window.Shopify.theme.ajaxCart.showDrawer === 'function'
        ) {
          window.Shopify.theme.ajaxCart.showDrawer(config);
        } else if (
          config.cart_action === 'modal_cart' &&
          window.Shopify.theme.ajaxCart &&
          typeof window.Shopify.theme.ajaxCart.showModal === 'function'
        ) {
          window.Shopify.theme.ajaxCart.showModal(config);
        }
      });
    } catch (e) {}
  }

  function bindWishlistAddToCartButtons(scope) {
    var root = scope || document;
    root.querySelectorAll('.js-wishlist-add-to-cart').forEach(function (btn) {
      if (btn.dataset.wishlistAtcBound) return;
      btn.dataset.wishlistAtcBound = '1';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var vid = btn.getAttribute('data-variant-id');
        if (!vid) return;
        var label = btn.textContent;
        btn.disabled = true;
        addVariantToCart(vid)
          .then(function () {
            afterWishlistAddToCart();
          })
          .catch(function () {})
          .then(function () {
            btn.disabled = false;
            btn.textContent = label;
          });
      });
    });
  }

  function fetchProduct(handle) {
    var h = encodeURIComponent(handle);
    var primary = getRoot() + 'products/' + h + '.js';
    var fallback = '/products/' + h + '.js';
    var urls = primary === fallback ? [primary] : [primary, fallback];

    function tryUrl(i) {
      if (i >= urls.length) return Promise.resolve(null);
      return fetch(urls[i], {
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      })
        .then(function (res) {
          if (!res.ok) return tryUrl(i + 1);
          return res.json().catch(function () {
            return tryUrl(i + 1);
          });
        })
        .catch(function () {
          return tryUrl(i + 1);
        });
    }
    return tryUrl(0).catch(function () {
      return null;
    });
  }

  function escapeHtml(s) {
    var d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }

  var wishlistPageRenderToken = 0;
  var wishlistPageScheduleTimer = null;

  /** Coalesce rapid calls; overlapping async renders used to clear the grid and leave it empty. */
  function scheduleRenderWishlistPage(root) {
    if (!root) root = document.getElementById('wishlist-page-root');
    if (!root) return;
    clearTimeout(wishlistPageScheduleTimer);
    wishlistPageScheduleTimer = setTimeout(function () {
      wishlistPageScheduleTimer = null;
      renderWishlistPage(root);
    }, 75);
  }

  function renderWishlistPage(root) {
    if (!root) return;
    var token = ++wishlistPageRenderToken;
    var handles = readHandles();
    var grid =
      root.querySelector('[data-wishlist-grid]') ||
      root.querySelector('#main-wishlist-product-grid');
    var emptyEl =
      root.querySelector('[data-wishlist-page-empty]') ||
      root.querySelector('[data-wishlist-empty]');
    var loading = root.querySelector('[data-wishlist-loading]');
    var allUrl = root.getAttribute('data-all-products-url') || '/collections/all';

    if (!grid) return;

    if (handles.length === 0) {
      if (token !== wishlistPageRenderToken) return;
      if (loading) loading.hidden = true;
      if (emptyEl) emptyEl.hidden = false;
      grid.innerHTML = '';
      return;
    }

    if (token !== wishlistPageRenderToken) return;
    if (emptyEl) emptyEl.hidden = true;
    if (loading) loading.hidden = false;
    grid.innerHTML = '';

    var saleLabel = root.getAttribute('data-sale-label') || 'Sale';
    var soldLabel = root.getAttribute('data-sold-label') || 'Sold out';
    var removeLabel = root.getAttribute('data-remove-label') || 'Remove';
    var addToCartLabel = addToCartButtonLabelFromEl(root);

    var chain = Promise.resolve();
    handles.forEach(function (handle) {
      chain = chain.then(function () {
        return fetchProduct(handle).then(function (product) {
          if (!product) {
            window.WishlistTheme.remove(handle);
            return;
          }
          try {
            var v = pickVariant(product);
            var priceC = priceToCents(v.price);
            var compareC = priceToCents(v.compare_at_price);
            var onSale = compareC > priceC && compareC > 0;
            var available = product.available !== false && v.available !== false;
            var img = '';
            if (typeof product.featured_image === 'string') {
              img = product.featured_image;
            } else if (product.featured_image && product.featured_image.src) {
              img = product.featured_image.src;
            } else if (product.images && product.images[0]) {
              var im0 = product.images[0];
              img = typeof im0 === 'string' ? im0 : im0.src || '';
            }
            if (img && img.indexOf('//') === 0) {
              img = 'https:' + img;
            }
            var title = product.title || '';
            var vendor = product.vendor || '';
            var url = product.url || '/products/' + handle;
            if (url.indexOf('http') !== 0 && url.charAt(0) !== '/') {
              url = '/' + url;
            }

            var badge = '';
            if (!available) {
              badge = '<div class="so icn">' + escapeHtml(soldLabel) + '</div>';
            } else if (onSale) {
              badge = '<div class="sale-item icn">' + escapeHtml(saleLabel) + '</div>';
            }

            var priceHtml =
              '<span class="theme-money">' +
              formatMoney(priceC) +
              '</span>';
            if (onSale && available) {
              priceHtml +=
                ' <span class="compare-at-price theme-money">' + formatMoney(compareC) + '</span>';
            }

            var vendorHtml = '';
            if (root.getAttribute('data-show-vendor') === 'true' && vendor) {
              vendorHtml = '<p class="product__vendor">' + escapeHtml(vendor) + '</p>';
            }

            var addToCartBtn = '';
            if (available && v.id) {
              addToCartBtn =
                '<button type="button" class="button add AddtoCart wishlist-card__add js-wishlist-add-to-cart" data-variant-id="' +
                String(v.id) +
                '">' +
                escapeHtml(addToCartLabel) +
                '</button>';
            }

            var card =
              '<div class="item product-index collection__page-product js-wishlist-card" data-product-handle="' +
              escapeHtml(handle) +
              '">' +
              '<div class="prod-container">' +
              badge +
              '<div class="prod-image image_square js-product-image">' +
              '<a href="' +
              escapeHtml(url) +
              '" title="' +
              escapeHtml(title) +
              '">' +
              '<div class="reveal">' +
              (img
                ? '<img src="' +
                  escapeHtml(img) +
                  '" alt="' +
                  escapeHtml(title) +
                  '" loading="lazy" width="600" height="600" style="width:100%;height:auto;">'
                : '') +
              '</div></a></div></div>' +
              '<div class="product-info">' +
              '<a href="' +
              escapeHtml(url) +
              '">' +
              vendorHtml +
              '<h2>' +
              escapeHtml(title) +
              '</h2></a>' +
              '<div class="wishlist-card__price">' +
              priceHtml +
              '</div>' +
              '<div class="wishlist-card__actions">' +
              addToCartBtn +
              '<button type="button" class="button secondary-button wishlist-card__remove js-wishlist-remove" data-product-handle="' +
              escapeHtml(handle) +
              '">' +
              escapeHtml(removeLabel) +
              '</button>' +
              '</div>' +
              '</div></div>';

            if (token === wishlistPageRenderToken) {
              grid.insertAdjacentHTML('beforeend', card);
            }
          } catch (e) {
            /* keep item in wishlist; skip card if payload is unexpected */
          }
        })
          .catch(function () {
            return null;
          });
      });
    });

    chain
      .then(function () {
        if (token !== wishlistPageRenderToken) return;
        if (loading) loading.hidden = true;
        if (readHandles().length === 0) {
          if (emptyEl) emptyEl.hidden = false;
        }
        if (window.AOS && typeof window.AOS.refresh === 'function') {
          window.AOS.refresh();
        }
        grid.querySelectorAll('.js-wishlist-remove').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var h = btn.getAttribute('data-product-handle');
            window.WishlistTheme.remove(h);
          });
        });
        bindWishlistAddToCartButtons(grid);
      })
      .catch(function () {
        if (token !== wishlistPageRenderToken) return;
        if (loading) loading.hidden = true;
      });
  }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  onReady(function () {
    // Initialize wishlist from customer metafield if logged in
    initializeWishlistFromMetafield();
    
    syncWishlistDrawerFooter();
    initWishlistDrawerTriggers();
    updateCountBadges();
    initToggles();
    renderWishlistDrawer();

    document.addEventListener('wishlist:updated', function () {
      refreshWishlistViews();
    });

    scheduleRenderWishlistPage();

    document.addEventListener('shopify:section:load', function (ev) {
      initWishlistDrawerTriggers();
      syncWishlistDrawerFooter();
      var pageRoot =
        (ev.target && ev.target.querySelector && ev.target.querySelector('#wishlist-page-root')) ||
        (ev.target && ev.target.id === 'wishlist-page-root' ? ev.target : null) ||
        document.getElementById('wishlist-page-root');
      if (pageRoot) {
        scheduleRenderWishlistPage(pageRoot);
      }
      renderWishlistDrawer();
    });

    window.addEventListener('storage', function (ev) {
      if (ev.key !== storageKey()) return;
      refreshWishlistViews();
    });

    window.addEventListener('load', function () {
      var r = document.getElementById('wishlist-page-root');
      if (!r) return;
      var grid = r.querySelector('[data-wishlist-grid]');
      if (!grid || readHandles().length === 0) return;
      if (!grid.querySelector('.js-wishlist-card')) {
        scheduleRenderWishlistPage(r);
      }
      renderWishlistDrawer();
    });
  });
})();
